import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "./api.js";
const agentsCache = new Map();
function resolveAgentsCacheKey(baseUrl) {
    return `${baseUrl ?? ""}`;
}
async function getAgentsCached(baseUrl, getAuthToken, bust) {
    const key = resolveAgentsCacheKey(baseUrl);
    if (bust) {
        agentsCache.delete(key);
    }
    const existing = agentsCache.get(key);
    if (existing?.data)
        return existing.data;
    if (existing?.promise)
        return existing.promise;
    const opts = { baseUrl, getToken: getAuthToken };
    const promise = api.listAgents(opts).then((agents) => {
        agentsCache.set(key, { data: agents });
        return agents;
    });
    agentsCache.set(key, { promise });
    return promise;
}
export function useAgents(baseUrl, getAuthToken, 
/** When false the hook skips fetching and returns an empty list. Defaults to true. */
enabled = true) {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);
    const [fetchKey, setFetchKey] = useState(0);
    const refetch = useCallback(() => {
        setLoading(true);
        setError(null);
        setFetchKey((k) => k + 1);
    }, []);
    useEffect(() => {
        if (!enabled)
            return;
        const bust = fetchKey > 0;
        getAgentsCached(baseUrl, getAuthToken, bust)
            .then(setAgents)
            .catch((err) => setError(err instanceof Error ? err.message : String(err)))
            .finally(() => setLoading(false));
    }, [fetchKey, enabled]);
    return { agents, loading, error, refetch };
}
export function useModels(baseUrl, getAuthToken, enabled = true) {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);
    const [fetchKey, setFetchKey] = useState(0);
    const refetch = useCallback(() => {
        setLoading(true);
        setError(null);
        setFetchKey((key) => key + 1);
    }, []);
    useEffect(() => {
        if (!enabled)
            return;
        const opts = { baseUrl, getToken: getAuthToken };
        api.listModels(opts)
            .then(setModels)
            .catch((err) => setError(err instanceof Error ? err.message : String(err)))
            .finally(() => setLoading(false));
    }, [baseUrl, enabled, fetchKey, getAuthToken]);
    return { models, loading, error, refetch };
}
// ─── useChat ────────────────────────────────────────────────────────
let idCounter = 0;
function nextId() {
    return `msg-${Date.now()}-${++idCounter}`;
}
export function useChat(baseUrl, agent, getAuthToken, options) {
    const [messages, setMessages] = useState([]);
    const [threadId, setThreadId] = useState();
    const [sending, setSending] = useState(false);
    const [runStatus, setRunStatus] = useState();
    const abortRef = useRef(null);
    const opts = { baseUrl, getToken: getAuthToken };
    const hasPendingConfirmation = messages.some((msg) => (msg.pendingConfirmation?.pendingToolCalls.length ?? 0) > 0);
    const hasPendingInput = messages.some((msg) => (msg.pendingInput?.requests.length ?? 0) > 0);
    const isAwaitingUser = hasPendingConfirmation || hasPendingInput;
    // Reset conversation
    const reset = useCallback(() => {
        abortRef.current?.abort();
        setMessages([]);
        setThreadId(undefined);
        setSending(false);
        setRunStatus(undefined);
    }, []);
    // ── Send ──────────────────────────────────────────────────────────
    const send = useCallback((text) => {
        if (isAwaitingUser)
            return;
        setRunStatus(undefined);
        const userMsg = {
            id: nextId(),
            role: "user",
            content: text,
            timestamp: new Date(),
        };
        const assistantId = nextId();
        const assistantMsg = {
            id: assistantId,
            role: "assistant",
            content: "",
            timestamp: new Date(),
            isStreaming: true,
            toolCalls: [],
            thinking: "",
        };
        setMessages((prev) => [...prev, userMsg, assistantMsg]);
        setSending(true);
        const controller = new AbortController();
        abortRef.current = controller;
        const updateAssistant = (updater) => {
            setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, ...updater(m) } : m));
        };
        const callbacks = buildStreamHandler({
            signal: controller.signal,
            updateMessage: updateAssistant,
            getCurrentThreadId: () => threadId,
            onThreadCreated: (id) => setThreadId(id),
            onAgentRouted: options?.onAgentRouted
                ? (target) => {
                    // Concierge routed — clear thread; parent re-renders the new agent
                    setThreadId(undefined);
                    options.onAgentRouted(target, text);
                }
                : undefined,
            setRunStatus,
            setSending,
        });
        // Dispatch by id presence: no thread → create; existing → resume.
        if (threadId) {
            void api.sendMessage(threadId, {
                agent,
                model: options?.model,
                reasoningEffort: options?.reasoningEffort,
                contextTier: options?.contextTier,
                message: text,
            }, callbacks, opts);
        }
        else {
            void api.createThread({
                agent,
                model: options?.model,
                reasoningEffort: options?.reasoningEffort,
                contextTier: options?.contextTier,
                message: text,
            }, callbacks, opts);
        }
    }, [agent, threadId, isAwaitingUser, options]);
    // ── Resolve (unified: confirmation approvals and/or interactive answers) ──
    // A single turn can have a pending confirmation AND a pending question form
    // (rare). Submitting both in one /confirm call avoids cancelling whichever
    // one isn't acted on — the backend resolves the whole batch from approvals
    // (confirm items) + answers (interactive items) together.
    const resolve = useCallback((messageId, runId, approvals, answers) => {
        if (!threadId)
            return;
        // Clear BOTH pending markers and reopen the bubble for the streaming
        // continuation (update-in-place rather than a second bubble).
        setMessages((prev) => prev.map((m) => m.id === messageId
            ? { ...m, pendingConfirmation: undefined, pendingInput: undefined, isStreaming: true }
            : m));
        setSending(true);
        const controller = new AbortController();
        abortRef.current = controller;
        // Seed tool-call list from the existing bubble so updates merge.
        const existing = messages.find((m) => m.id === messageId);
        const seedToolCalls = existing?.toolCalls
            ? [...existing.toolCalls]
            : [];
        const updateOrigMsg = (updater) => {
            setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, ...updater(m) } : m));
        };
        const callbacks = buildStreamHandler({
            signal: controller.signal,
            updateMessage: updateOrigMsg,
            getCurrentThreadId: () => threadId,
            onThreadCreated: () => { },
            setRunStatus,
            setSending,
            seedToolCalls,
            approvalsForDenialDetection: approvals,
        });
        void api.confirmThread(threadId, {
            agent,
            model: options?.model,
            reasoningEffort: options?.reasoningEffort,
            contextTier: options?.contextTier,
            runId,
            approvals,
            answers,
        }, callbacks, opts);
    }, [threadId, agent, messages, opts]);
    // Thin wrappers for the single-pending cases.
    const confirm = useCallback((messageId, runId, approvals) => resolve(messageId, runId, approvals), [resolve]);
    const respond = useCallback((messageId, runId, answers) => resolve(messageId, runId, {}, answers), [resolve]);
    return { messages, threadId, sending, runStatus, send, confirm, respond, resolve, reset, hasPendingConfirmation, hasPendingInput };
}
function buildStreamHandler(handler) {
    const toolCalls = handler.seedToolCalls ? [...handler.seedToolCalls] : [];
    let pendingConfirmation;
    let pendingInput;
    let routingTarget;
    return {
        signal: handler.signal,
        async onEvent(event) {
            switch (event.event) {
                case "thinking": {
                    const content = event.data.content ?? "";
                    handler.updateMessage((prev) => ({
                        thinking: (prev.thinking ?? "") + content,
                    }));
                    break;
                }
                case "message_delta": {
                    const content = event.data.content ?? "";
                    handler.updateMessage((prev) => ({
                        content: prev.content + content,
                    }));
                    break;
                }
                case "tool_call_start": {
                    const tc = {
                        toolCallId: event.data.toolCallId ?? "",
                        toolName: event.data.toolName ?? "unknown",
                        arguments: event.data.arguments ?? {},
                        status: "calling",
                    };
                    const idx = toolCalls.findIndex((t) => t.toolCallId === tc.toolCallId);
                    if (idx >= 0) {
                        toolCalls[idx] = tc;
                    }
                    else {
                        toolCalls.push(tc);
                    }
                    handler.updateMessage(() => ({ toolCalls: [...toolCalls] }));
                    break;
                }
                case "tool_call_result": {
                    const id = event.data.toolCallId ?? "";
                    const existing = toolCalls.find((t) => t.toolCallId === id);
                    if (existing) {
                        existing.result = event.data.result;
                        existing.status =
                            handler.approvalsForDenialDetection?.[id] === false
                                ? "denied"
                                : "done";
                    }
                    // Detect concierge routing signal
                    if (event.data.toolName === "route_to_agent") {
                        const result = event.data.result;
                        if (result?.routed && typeof result.targetAgent === "string") {
                            routingTarget = result.targetAgent;
                        }
                    }
                    handler.updateMessage(() => ({ toolCalls: [...toolCalls] }));
                    break;
                }
                case "confirmation_required": {
                    pendingConfirmation = {
                        runId: event.data.runId ?? "",
                        pendingToolCalls: event.data.pendingToolCalls ??
                            [],
                    };
                    handler.updateMessage(() => ({
                        isStreaming: false,
                        pendingConfirmation,
                    }));
                    handler.setSending(false);
                    break;
                }
                case "input_required": {
                    pendingInput = {
                        runId: event.data.runId ?? "",
                        requests: event.data.requests ?? [],
                    };
                    handler.updateMessage(() => ({
                        isStreaming: false,
                        pendingInput,
                    }));
                    handler.setSending(false);
                    break;
                }
                case "auth_required": {
                    const authData = {
                        provider: event.data.provider ?? "",
                        authorizeUrl: event.data.authorizeUrl ?? "",
                        message: event.data.message ?? "",
                    };
                    handler.updateMessage(() => ({
                        content: authData.message,
                        authRequired: authData,
                        isStreaming: false,
                    }));
                    handler.setSending(false);
                    break;
                }
                case "run_status": {
                    const status = event.data.status ?? "";
                    // The create stream's first event is `status: "thread_created"`
                    // and carries the new threadId so the UI can pin subsequent
                    // calls before any model output arrives.
                    if (status === "thread_created") {
                        const newId = event.data.threadId;
                        if (newId)
                            handler.onThreadCreated(newId);
                    }
                    handler.setRunStatus(status || undefined);
                    break;
                }
                case "done": {
                    if (routingTarget && handler.onAgentRouted) {
                        handler.updateMessage(() => ({ isStreaming: false }));
                        handler.setRunStatus(undefined);
                        handler.setSending(false);
                        handler.onAgentRouted(routingTarget);
                    }
                    else {
                        const newThreadId = event.data.threadId ?? handler.getCurrentThreadId();
                        if (newThreadId && newThreadId !== handler.getCurrentThreadId()) {
                            handler.onThreadCreated(newThreadId);
                        }
                        handler.updateMessage(() => ({
                            isStreaming: false,
                            pendingConfirmation,
                            pendingInput,
                        }));
                        handler.setRunStatus(undefined);
                        handler.setSending(false);
                    }
                    break;
                }
                case "error": {
                    const message = event.data.message ?? "Unknown error";
                    handler.updateMessage((prev) => ({
                        content: prev.content
                            ? prev.content + `\n\nError: ${message}`
                            : `Error: ${message}`,
                        isStreaming: false,
                    }));
                    handler.setRunStatus(undefined);
                    handler.setSending(false);
                    break;
                }
            }
        },
        onError(err) {
            if (handler.signal.aborted)
                return;
            handler.updateMessage(() => ({
                content: `Stream error: ${err instanceof Error ? err.message : String(err)}`,
                isStreaming: false,
            }));
            handler.setRunStatus(undefined);
            handler.setSending(false);
        },
        onClose() {
            handler.updateMessage(() => ({ isStreaming: false }));
            handler.setRunStatus(undefined);
            handler.setSending(false);
        },
    };
}
//# sourceMappingURL=hooks.js.map